export type InvitationStatusFilter =
  | "all"
  | "not_sent"
  | "confirmed"
  | "awaiting_reply"
  | "in_progress"
  | "invalid"
  | "failed_contact"
  | "failed_system"
  | "scanned"
  | "declined"

export type ReminderStatusFilter =
  | "all"
  | "not_sent"
  | "sent"
  | "failed_contact"
  | "failed_system"

export type ThankStatusFilter =
  | "all"
  | "not_sent"
  | "sent"
  | "failed_contact"
  | "failed_system"

export interface InvitationFilterOption {
  id: InvitationStatusFilter
  label: string
  statusNumber: string
  waStatusNumber: string
  /**
   * When set, the guest must also have an invitation_failure_type matching
   * this value (in addition to waStatusNumber). Used to split the legacy
   * "Delivery issues" bucket into contact- vs system-side failures.
   */
  failureType?: "contact" | "system"
}

export interface ReminderFilterOption {
  id: ReminderStatusFilter
  label: string
}

export const INVITATION_FILTERS: InvitationFilterOption[] = [
  { id: "all", label: "All", statusNumber: "", waStatusNumber: "" },
  { id: "not_sent", label: "Not sent yet", statusNumber: "", waStatusNumber: "0" },
  { id: "confirmed", label: "Confirmed", statusNumber: "1", waStatusNumber: "" },
  { id: "awaiting_reply", label: "Awaiting reply", statusNumber: "", waStatusNumber: "2,3,4" },
  { id: "in_progress", label: "In progress", statusNumber: "", waStatusNumber: "1" },
  { id: "invalid", label: "Invalid RSVP", statusNumber: "3", waStatusNumber: "" },
  {
    id: "failed_contact",
    label: "Invalid contacts",
    statusNumber: "",
    waStatusNumber: "5",
    failureType: "contact",
  },
  {
    id: "failed_system",
    label: "Failed",
    statusNumber: "",
    waStatusNumber: "5",
    failureType: "system",
  },
  { id: "scanned", label: "Scanned", statusNumber: "4", waStatusNumber: "" },
  { id: "declined", label: "Declined", statusNumber: "2", waStatusNumber: "" },
]

export const isScannedGuest = (guest: any): boolean =>
  guest.status === 4 ||
  guest.scanned === true ||
  guest.is_scanned === true ||
  Boolean(guest.scanned_at || guest.checked_in_at)

export const REMINDER_FILTERS: ReminderFilterOption[] = [
  { id: "all", label: "All" },
  { id: "not_sent", label: "Not sent" },
  { id: "sent", label: "Sent" },
  { id: "failed_contact", label: "Invalid contacts" },
  { id: "failed_system", label: "Failed" },
]

export const THANK_FILTERS: { id: ThankStatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "not_sent", label: "Not sent" },
  { id: "sent", label: "Sent" },
  { id: "failed_contact", label: "Invalid contacts" },
  { id: "failed_system", label: "Failed" },
]

/**
 * Shared matcher for the new split-failure statuses. Returns true when the
 * guest's status + failure_type combination matches the active filter.
 *
 *   failed_contact → status === 'failed' AND failure_type === 'contact'
 *   failed_system  → status === 'failed' AND failure_type === 'system'
 *   everything else → straight equality against statusField
 */
export const matchesStatusWithFailureType = (
  guest: any,
  statusField: string,
  failureTypeField: string,
  filterId: string,
): boolean => {
  if (filterId === "all") return true

  const status = guest[statusField]
  const failureType = guest[failureTypeField]

  if (filterId === "failed_contact") {
    return status === "failed" && failureType === "contact"
  }
  if (filterId === "failed_system") {
    return (
      status === "failed" && (failureType === "system" || failureType == null)
    )
  }
  if (filterId === "not_sent") {
    return !status || status === "not_sent"
  }
  return status === filterId
}

/**
 * When filtering by WA delivery status, exclude guests whose RSVP status
 * has already resolved — their RSVP decision takes priority over the
 * WhatsApp delivery pipeline status.
 */
export const matchesInvitationFilter = (
  guest: any,
  filter: InvitationFilterOption,
): boolean => {
  if (filter.id === "all") return true
  if (filter.id === "scanned") return isScannedGuest(guest)
  if (filter.statusNumber !== "") {
    const statuses = filter.statusNumber.split(",").map(Number)
    return statuses.includes(guest.status)
  }
  if (filter.waStatusNumber !== "") {
    const waStatuses = filter.waStatusNumber.split(",").map(Number)
    const matchesWa = waStatuses.includes(guest.whatsapp_invitation_status ?? 0)
    if (!matchesWa) return false
    if (guest.status === 1 || guest.status === 2 || guest.status === 3) return false

    if (filter.failureType) {
      const failureType = guest.invitation_failure_type as
        | "contact"
        | "system"
        | null
        | undefined
      if (filter.failureType === "system") {
        // Treat an unknown/null reason as a system error so no failed
        // invitation ever falls outside of a filter bucket.
        return failureType === "system" || failureType == null
      }
      return failureType === filter.failureType
    }
    return true
  }
  return true
}
